import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Textbox, Circle, Rect, Text as FabricText } from 'fabric';
import { toast } from 'sonner';
import axios from 'axios';
import { CanvasWorkspace } from '@/components/canvas-editor/CanvasWorkspace';
import { useCanvasHistory } from '@/components/canvas-editor/hooks/useCanvasHistory';
import { useCanvasExport } from '@/components/canvas-editor/hooks/useCanvasExport';
import { ASPECT_RATIOS } from '@/components/canvas-editor/utils/canvas-helpers';
import { ComposerModal } from '@/components/ComposerModal';
import { useCanvasUpload } from '@/hooks/useCanvasUpload';

// Map Q-TIP format labels to ASPECT_RATIOS keys
const FORMATS = [
  { label: 'Post 1:1',   key: '1:1'  },
  { label: 'Story 9:16', key: '9:16' },
  { label: '4:5',        key: '4:5'  },
  { label: 'Cover',      key: '16:9' },
];

const LEFT_TABS = ['PHOTOS', 'AI GEN', 'AR', 'TEXT', 'BG'];

const PHOTO_TEMPLATES = [
  {
    label: 'MAGAZINE', thumbText: 'COVER STORY',
    bg: 'linear-gradient(160deg, #0d0800 0%, #1a0f00 50%, #0d0800 100%)',
    preset: {
      background: 'linear-gradient(160deg, #0d0800 0%, #1a0f00 50%, #0d0800 100%)',
      text: 'COVER STORY\nYOUR NAME\nHERE', font: 'Bebas Neue, sans-serif', fontSize: 36,
      textColor: '#EAE0CC', textAlign: 'left',
      accent: { text: 'BLVX MAGAZINE', color: '#C9950C', fontSize: 10, position: 'top-left' },
    },
  },
  {
    label: 'GLOW PORTRAIT', thumbText: '👤',
    bg: 'radial-gradient(ellipse at center, #1a0030 0%, #0a0015 60%, #050608 100%)',
    preset: {
      background: 'radial-gradient(ellipse at center, #1a0030 0%, #0a0015 60%, #050608 100%)',
      text: '', font: 'Playfair Display, serif', fontSize: 28, textColor: '#EAE0CC',
      showPhotoPlaceholder: true, placeholderText: 'Tap camera to add your photo',
    },
  },
  {
    label: 'FIRE FRAME', thumbText: '🔥',
    bg: 'linear-gradient(135deg, #1a0800 0%, #2d1200 50%, #1a0500 100%)',
    preset: {
      background: 'linear-gradient(135deg, #1a0800 0%, #2d1200 50%, #1a0500 100%)',
      text: '🔥', font: 'Bebas Neue, sans-serif', fontSize: 80, textColor: '#F07B1D',
    },
  },
  {
    label: 'GOLD LUXURY', thumbText: 'LUXURY',
    bg: 'linear-gradient(160deg, #1a1400 0%, #2d2200 40%, #1a1000 100%)',
    preset: {
      background: 'linear-gradient(160deg, #1a1400 0%, #2d2200 40%, #1a1000 100%)',
      text: 'LUXURY', font: 'Bebas Neue, sans-serif', fontSize: 52, textColor: '#C9950C', textAlign: 'center',
    },
  },
  {
    label: 'HBCU PRIDE', thumbText: 'HBCU',
    bg: 'linear-gradient(135deg, #001a08 0%, #002d14 100%)',
    preset: {
      background: 'linear-gradient(135deg, #001a08 0%, #002d14 100%)',
      text: 'HBCU\nPROUD', font: 'Bebas Neue, sans-serif', fontSize: 48, textColor: '#18B574', textAlign: 'center',
    },
  },
  {
    label: 'COLLAGE 2×2', thumbText: '⊞',
    bg: '#0F1219',
    preset: {
      background: '#0F1219', text: '', showCollageGrid: true, gridSlots: 4,
    },
  },
];

const PHOTO_EFFECTS = ['☀️ Original', '◼ B&W', '🟫 Sepia', '⚡ Vivid'];
const AI_STYLES = ['Cinematic', 'Abstract', 'Minimalist', 'Luxury', 'Street', 'HBCU Yard'];

const FONTS = [
  { name: 'Playfair',   tag: 'Serif',   family: 'Playfair Display, serif',        style: {} },
  { name: 'BEBAS',      tag: 'Display', family: 'Bebas Neue, sans-serif',          style: { letterSpacing: '2px' } },
  { name: 'Instrument', tag: 'Sans',    family: 'Instrument Sans, sans-serif',     style: {} },
  { name: 'DM Serif',   tag: 'Serif',   family: 'DM Serif Display, serif',         style: { fontStyle: 'italic' } },
  { name: 'MONO',       tag: 'Mono',    family: 'Azeret Mono, monospace',          style: {} },
  { name: 'Righteous',  tag: 'Bold',    family: 'Righteous, sans-serif',           style: {} },
];

const COLOR_SWATCHES = ['#EAE0CC', '#FFFFFF', '#e8e8e8', '#F07B1D', '#D44F2E', '#18B574', '#38bdf8', '#A855F7', '#F59E0B', '#050608'];

let layerCounter = 0;

const LabPage = () => {
  const navigate = useNavigate();
  const fabricRef = useRef(null);
  const fileInputRef = useRef(null);

  const [activeFormat, setActiveFormat] = useState(FORMATS[0]);
  const [background, setBackground] = useState({ type: 'solid', value: '#151A24' });
  const [selectedObject, setSelectedObject] = useState(null);
  const [activeLeftTab, setActiveLeftTab] = useState('PHOTOS');
  const [selectedFont, setSelectedFont] = useState(FONTS[0]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedAiStyle, setSelectedAiStyle] = useState(null);
  const [bonitaPrompt, setBonitaPrompt] = useState('');
  const [exporting, setExporting] = useState(false);
  const [composerMedia, setComposerMedia] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiPreviews, setAiPreviews] = useState([]);
  const [selectedAiPreview, setSelectedAiPreview] = useState(null);
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasteUrlLoading, setPasteUrlLoading] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState('photos');

  const aspectRatio = ASPECT_RATIOS.find(ar => ar.key === activeFormat.key) || ASPECT_RATIOS[0];
  const { saveState, undo, redo, canUndo, canRedo } = useCanvasHistory(fabricRef);
  const { exportAsBlob } = useCanvasExport(fabricRef);
  const { uploadCanvas } = useCanvasUpload();

  const configureObject = useCallback((obj) => {
    obj.set({ cornerSize: 12, touchCornerSize: 40, cornerColor: '#F07B1D', borderColor: '#F07B1D', transparentCorners: false });
    obj.__id = `layer-${++layerCounter}`;
    return obj;
  }, []);

  const addText = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const text = new Textbox('Type something...', {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fontSize: 48,
      fontFamily: selectedFont.family,
      fill: '#EAE0CC',
      textAlign: 'center',
      width: canvas.width * 0.6,
      editable: true,
    });
    configureObject(text);
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    saveState();
  }, [fabricRef, configureObject, saveState, selectedFont]);

  const applyFont = useCallback((font) => {
    setSelectedFont(font);
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (active?.type === 'textbox') {
      active.set('fontFamily', font.family);
      canvas.renderAll();
      saveState();
    }
  }, [fabricRef, saveState]);

  const applyColor = useCallback((color) => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (active?.type === 'textbox') {
      active.set('fill', color);
      canvas.renderAll();
      saveState();
    }
  }, [fabricRef, saveState]);

  const handleDropToBlock = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await exportAsBlob();
      const { url } = await uploadCanvas(blob);
      const preview = URL.createObjectURL(blob);
      setComposerMedia({ url, preview, type: 'image' });
      setComposerOpen(true);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  }, [exportAsBlob, uploadCanvas]);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setBackground({ type: 'image', value: url });
    saveState();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [saveState]);

  // Apply a design (from AI or templates) to the canvas
  const applyDesignToCanvas = useCallback((design) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // Clear existing objects
    canvas.clear();

    // Set background
    if (design.background) {
      setBackground(
        design.background.includes('gradient')
          ? { type: 'gradient', value: design.background }
          : { type: 'solid', value: design.background }
      );
    }

    // Add main text
    if (design.text) {
      const text = new Textbox(design.text, {
        left: canvas.width / 2,
        top: canvas.height / 2,
        originX: 'center',
        originY: 'center',
        fontFamily: design.font || 'Bebas Neue, sans-serif',
        fontSize: design.fontSize || 36,
        fill: design.textColor || '#EAE0CC',
        textAlign: design.textAlign || 'center',
        width: canvas.width * 0.75,
        editable: true,
      });
      configureObject(text);
      canvas.add(text);
    }

    // Accent text (e.g. BLVX MAGAZINE corner)
    if (design.accent) {
      const accent = new Textbox(design.accent.text, {
        left: design.accent.position === 'top-left' ? 24 : canvas.width - 24,
        top: 24,
        originX: design.accent.position === 'top-left' ? 'left' : 'right',
        originY: 'top',
        fontFamily: "'Azeret Mono', monospace",
        fontSize: design.accent.fontSize || 10,
        fill: design.accent.color || '#C9950C',
        letterSpacing: 200,
        editable: true,
        width: 200,
      });
      configureObject(accent);
      canvas.add(accent);
    }

    // Photo placeholder (circle with dashed border)
    if (design.showPhotoPlaceholder) {
      const circle = new Circle({
        radius: 100,
        left: canvas.width / 2,
        top: canvas.height / 2,
        originX: 'center',
        originY: 'center',
        fill: 'transparent',
        stroke: 'rgba(124,79,212,.4)',
        strokeWidth: 2,
        strokeDashArray: [8, 4],
      });
      canvas.add(circle);

      if (design.placeholderText) {
        const hint = new FabricText(design.placeholderText, {
          left: canvas.width / 2,
          top: canvas.height / 2 + 120,
          originX: 'center',
          originY: 'center',
          fontFamily: "'Azeret Mono', monospace",
          fontSize: 10,
          fill: 'rgba(255,255,255,.3)',
          textAlign: 'center',
        });
        canvas.add(hint);
      }
    }

    // Collage grid (2x2 dashed slots)
    if (design.showCollageGrid) {
      const slotSize = canvas.width / 2 - 4;
      const positions = [
        { left: 2, top: 2 },
        { left: slotSize + 6, top: 2 },
        { left: 2, top: slotSize + 6 },
        { left: slotSize + 6, top: slotSize + 6 },
      ];
      positions.forEach(pos => {
        const rect = new Rect({
          ...pos,
          width: slotSize,
          height: slotSize,
          fill: 'rgba(255,255,255,.03)',
          stroke: 'rgba(255,255,255,.1)',
          strokeWidth: 1,
          strokeDashArray: [6, 4],
          rx: 8,
          ry: 8,
        });
        canvas.add(rect);
      });
    }

    canvas.renderAll();
    saveState();
  }, [fabricRef, configureObject, saveState]);

  // AI Generate handler — Pollinations.ai (free, no API key)
  const handleAIGenerate = useCallback(() => {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    setAiPreviews([]);
    setSelectedAiPreview(null);

    const fullPrompt = [
      aiPrompt,
      selectedAiStyle || '',
      'high quality, dramatic lighting, dark aesthetic, 1:1 square format',
    ].filter(Boolean).join(', ');

    const encoded = encodeURIComponent(fullPrompt);
    const seeds = Array.from({ length: 4 }, () => Math.floor(Math.random() * 99999));
    const urls = seeds.map(seed =>
      `https://image.pollinations.ai/prompt/${encoded}?width=512&height=512&seed=${seed}&nologo=true`
    );

    setAiPreviews(urls);
    setAiLoading(false);
    toast.success('Generating 4 variations — tap one to apply');
  }, [aiPrompt, aiLoading, selectedAiStyle]);

  // Apply AI-generated image as canvas background
  const applyAIBackground = useCallback((imageUrl, index) => {
    setSelectedAiPreview(index);
    const canvas = fabricRef.current;
    if (!canvas) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const { Image: FabricImage } = require('fabric');
      const fabricImg = new FabricImage(img, {
        left: canvas.width / 2,
        top: canvas.height / 2,
        originX: 'center',
        originY: 'center',
      });
      const scaleX = canvas.width / (img.width || 512);
      const scaleY = canvas.height / (img.height || 512);
      const scale = Math.max(scaleX, scaleY);
      fabricImg.scaleX = scale;
      fabricImg.scaleY = scale;

      // Remove existing background images, keep text layers
      canvas.getObjects('image').forEach(obj => canvas.remove(obj));

      canvas.add(fabricImg);
      canvas.sendObjectToBack(fabricImg);
      canvas.renderAll();
      saveState();
      toast.success('AI background applied');
    };
    img.onerror = () => {
      toast.error("Image still loading — try again in a few seconds");
    };
    img.src = imageUrl;
  }, [fabricRef, saveState]);

  // Media handlers for camera/upload
  const handleMediaFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const canvas = fabricRef.current;
      if (!canvas) return;
      const img = new Image();
      img.onload = () => {
        const { Image: FabricImage } = require('fabric');
        const fabricImg = new FabricImage(img, {
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: 'center',
          originY: 'center',
        });
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        fabricImg.scaleX = scale;
        fabricImg.scaleY = scale;
        configureObject(fabricImg);
        canvas.add(fabricImg);
        canvas.setActiveObject(fabricImg);
        canvas.renderAll();
        saveState();
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, [fabricRef, configureObject, saveState]);

  const handleVideoFile = useCallback((file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = url;
    video.currentTime = 0.1;
    video.onloadeddata = () => {
      const canvas = fabricRef.current;
      if (!canvas) return;
      const { Image: FabricImage } = require('fabric');
      const fabricImg = new FabricImage(video, {
        left: canvas.width / 2,
        top: canvas.height / 2,
        originX: 'center',
        originY: 'center',
      });
      const scale = Math.min(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
      fabricImg.scaleX = scale;
      fabricImg.scaleY = scale;
      configureObject(fabricImg);
      canvas.add(fabricImg);
      canvas.renderAll();
      setVideoFile(file);
      saveState();
    };
    video.load();
  }, [fabricRef, configureObject, saveState]);

  const handleUrlPaste = useCallback(async (url) => {
    if (!url.trim()) return;
    setPasteUrlLoading(true);
    try {
      const canvas = fabricRef.current;
      if (!canvas) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const { Image: FabricImage } = require('fabric');
        const fabricImg = new FabricImage(img, {
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: 'center',
          originY: 'center',
        });
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        fabricImg.scaleX = scale;
        fabricImg.scaleY = scale;
        configureObject(fabricImg);
        canvas.add(fabricImg);
        canvas.setActiveObject(fabricImg);
        canvas.renderAll();
        setPasteUrl('');
        saveState();
        setPasteUrlLoading(false);
      };
      img.onerror = () => {
        toast.error("Couldn't load that image — try saving and uploading instead");
        setPasteUrlLoading(false);
      };
      img.src = url;
    } catch {
      toast.error("Couldn't load that image");
      setPasteUrlLoading(false);
    }
  }, [fabricRef, configureObject, saveState]);

  const addArSticker = useCallback((emoji) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const text = new FabricText(emoji, {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fontSize: 60,
    });
    configureObject(text);
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    saveState();
  }, [fabricRef, configureObject, saveState]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); redo(); }
      if ((e.key === 'Delete' || e.key === 'Backspace')) {
        const canvas = fabricRef.current;
        const active = canvas?.getActiveObject();
        if (active && !active.isEditing) { canvas.remove(active); canvas.renderAll(); saveState(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, saveState]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 60,
      background: '#0F1219',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ── TOPBAR ── */}
      <div className="lab-topbar" style={{
        height: '52px', background: '#090B10',
        borderBottom: '1px solid rgba(255,255,255,0.055)',
        display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px',
        flexShrink: 0, zIndex: 100,
      }}>
        {/* Logo */}
        <a href="/home" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#EAE0CC', textDecoration: 'none' }}>
          BL<span style={{ color: '#F07B1D' }}>VX</span>
        </a>

        {/* Desktop-only: divider, breadcrumb, format switcher */}
        <div className="lab-desktop-only" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.09)' }} />
          <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#72788A', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ cursor: 'pointer' }} onClick={() => navigate('/home')}>Home</span>
            <span style={{ color: '#3A3F4E' }}>›</span>
            <span style={{ color: '#F07B1D' }}>The Lab</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', background: '#151A24', border: '1px solid rgba(255,255,255,0.055)', borderRadius: '8px', padding: '3px' }}>
            {FORMATS.map(fmt => (
              <button
                key={fmt.label}
                onClick={() => setActiveFormat(fmt)}
                style={{
                  fontFamily: "'Azeret Mono', monospace", fontSize: '10px', letterSpacing: '.5px',
                  color: activeFormat.label === fmt.label ? '#F07B1D' : '#72788A',
                  padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', border: 'none',
                  background: activeFormat.label === fmt.label ? '#1C2230' : 'transparent',
                  transition: 'all .14s',
                }}
              >
                {fmt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right side */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={undo} disabled={!canUndo}
            className="lab-desktop-only"
            style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#151A24', border: '1px solid rgba(255,255,255,0.055)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: canUndo ? '#C8BCA8' : '#3A3F4E', transition: 'all .14s' }}
          >↩</button>
          <button
            onClick={redo} disabled={!canRedo}
            className="lab-desktop-only"
            style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#151A24', border: '1px solid rgba(255,255,255,0.055)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: canRedo ? '#C8BCA8' : '#3A3F4E', transition: 'all .14s' }}
          >↪</button>

          {/* Drop to Block */}
          <button
            onClick={handleDropToBlock} disabled={exporting}
            style={{
              background: '#F07B1D', color: '#050608',
              fontFamily: "'Bebas Neue', sans-serif", fontSize: '15px', letterSpacing: '1.5px',
              padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
              boxShadow: '0 3px 16px rgba(240,123,29,.3)', transition: 'all .15s',
              opacity: exporting ? 0.6 : 1,
            }}
          >
            🍽 {exporting ? 'Dropping...' : 'Drop to Block'}
          </button>
        </div>
      </div>

      {/* ── MAIN BODY ── */}
      <div className="lab-main-body" style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* LEFT PANEL */}
        <div className="lab-lpanel" style={{
          width: '252px', flexShrink: 0,
          background: '#090B10', borderRight: '1px solid rgba(255,255,255,0.055)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.055)', flexShrink: 0 }}>
            {LEFT_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveLeftTab(tab)}
                style={{
                  flex: 1, padding: '10px 4px',
                  fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1.5px',
                  textTransform: 'uppercase', cursor: 'pointer', border: 'none',
                  background: 'transparent',
                  color: activeLeftTab === tab ? '#F07B1D' : '#72788A',
                  borderBottom: activeLeftTab === tab ? '2px solid #F07B1D' : '2px solid transparent',
                  transition: 'all .14s',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {activeLeftTab === 'PHOTOS' && (
              <>
                {/* Section label */}
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
                  Templates
                </div>
                {/* Template grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '16px' }}>
                  {PHOTO_TEMPLATES.map(t => (
                    <div
                      key={t.label}
                      onClick={() => t.preset ? applyDesignToCanvas(t.preset) : setBackground({ type: 'gradient', value: t.bg })}
                      style={{
                        aspectRatio: '1', borderRadius: '8px', cursor: 'pointer',
                        background: t.bg, border: '1px solid rgba(255,255,255,0.055)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .14s', gap: '4px',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(240,123,29,0.22)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.055)'; e.currentTarget.style.transform = 'none'; }}
                    >
                      <span style={{ fontSize: '16px' }}>{t.thumbText}</span>
                      <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '7px', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>{t.label}</span>
                    </div>
                  ))}
                </div>

                {/* Media picker — 3 buttons */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                    {/* Camera — photo */}
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.capture = 'environment';
                        input.onchange = (e) => handleMediaFile(e.target.files[0]);
                        input.click();
                      }}
                      style={{
                        background: '#151A24', border: '1px solid rgba(255,255,255,.055)',
                        borderRadius: '8px', padding: '10px 6px', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                        transition: 'all .14s', color: '#72788A',
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>📷</span>
                      <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1px', textTransform: 'uppercase' }}>Photo</span>
                    </button>
                    {/* Camera — video */}
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'video/*';
                        input.capture = 'environment';
                        input.onchange = (e) => handleVideoFile(e.target.files[0]);
                        input.click();
                      }}
                      style={{
                        background: '#151A24', border: '1px solid rgba(255,255,255,.055)',
                        borderRadius: '8px', padding: '10px 6px', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                        transition: 'all .14s', color: '#72788A',
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>🎥</span>
                      <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1px', textTransform: 'uppercase' }}>Video</span>
                    </button>
                    {/* Gallery / file picker */}
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*,video/*';
                        input.onchange = (e) => {
                          const file = e.target.files[0];
                          if (file?.type?.startsWith('video/')) handleVideoFile(file);
                          else handleMediaFile(file);
                        };
                        input.click();
                      }}
                      style={{
                        background: '#151A24', border: '1px solid rgba(255,255,255,.055)',
                        borderRadius: '8px', padding: '10px 6px', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                        transition: 'all .14s', color: '#72788A',
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>🗂</span>
                      <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1px', textTransform: 'uppercase' }}>Gallery</span>
                    </button>
                  </div>
                  {/* URL paste field */}
                  <div style={{ position: 'relative' }}>
                    <input
                      placeholder="Paste image URL..."
                      value={pasteUrl}
                      onChange={e => setPasteUrl(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleUrlPaste(pasteUrl); }}
                      style={{
                        width: '100%', background: '#151A24',
                        border: '1px solid rgba(255,255,255,.055)',
                        borderRadius: '6px', padding: '8px 36px 8px 10px',
                        fontFamily: "'Azeret Mono', monospace", fontSize: '10px',
                        color: '#EAE0CC', outline: 'none', boxSizing: 'border-box',
                        transition: 'border-color .15s',
                      }}
                    />
                    <button
                      onClick={() => handleUrlPaste(pasteUrl)}
                      disabled={pasteUrlLoading}
                      style={{
                        position: 'absolute', right: '4px', top: '50%',
                        transform: 'translateY(-50%)',
                        background: '#F07B1D', border: 'none', borderRadius: '4px',
                        padding: '4px 8px', cursor: 'pointer',
                        fontFamily: "'Azeret Mono', monospace", fontSize: '9px',
                        color: '#050608', fontWeight: 600,
                      }}
                    >
                      {pasteUrlLoading ? '...' : 'GO'}
                    </button>
                  </div>
                  <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', color: '#3A3F4E', marginTop: '5px', lineHeight: 1.5 }}>
                    Paste URL = load any image from the web onto your canvas
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />

                {/* Effects */}
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
                  Effects
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {PHOTO_EFFECTS.map(fx => (
                    <button
                      key={fx}
                      style={{
                        padding: '7px 6px', borderRadius: '6px',
                        fontFamily: "'Azeret Mono', monospace", fontSize: '9px',
                        color: '#72788A', background: '#151A24',
                        border: '1px solid rgba(255,255,255,0.055)', cursor: 'pointer',
                      }}
                    >
                      {fx}
                    </button>
                  ))}
                </div>
              </>
            )}

            {activeLeftTab === 'AI GEN' && (
              <>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
                  Describe the vibe
                </div>
                <textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder="'gold luxury portrait with fire'"
                  style={{
                    width: '100%', background: '#151A24',
                    border: '1px solid rgba(124,79,212,0.25)', borderRadius: '8px',
                    padding: '10px', fontFamily: "'Azeret Mono', monospace", fontSize: '11px',
                    color: '#EAE0CC', outline: 'none', resize: 'none', minHeight: '80px',
                    marginBottom: '12px', transition: 'border-color .15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(124,79,212,0.5)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(124,79,212,0.25)'; }}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '12px' }}>
                  {AI_STYLES.map(s => (
                    <button
                      key={s}
                      onClick={() => setSelectedAiStyle(s === selectedAiStyle ? null : s)}
                      style={{
                        padding: '4px 10px', borderRadius: '20px',
                        fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '.5px',
                        cursor: 'pointer', transition: 'all .14s',
                        color: selectedAiStyle === s ? '#7C4FD4' : '#72788A',
                        background: selectedAiStyle === s ? 'rgba(124,79,212,0.1)' : 'transparent',
                        border: selectedAiStyle === s ? '1px solid rgba(124,79,212,0.3)' : '1px solid rgba(255,255,255,0.055)',
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleAIGenerate}
                  disabled={aiLoading || !aiPrompt.trim()}
                  style={{
                    width: '100%',
                    background: aiLoading ? 'rgba(124,79,212,.5)' : '#7C4FD4',
                    color: '#fff',
                    fontFamily: "'Bebas Neue', sans-serif", fontSize: '15px', letterSpacing: '1.5px',
                    border: 'none', borderRadius: '8px', padding: '11px',
                    cursor: aiLoading ? 'not-allowed' : 'pointer',
                    transition: 'all .2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    opacity: !aiPrompt.trim() ? 0.5 : 1,
                    marginBottom: '10px',
                  }}
                >
                  {aiLoading ? (
                    <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>◌</span> Generating...</>
                  ) : (
                    '✧ Generate with AI'
                  )}
                </button>
                {aiError && (
                  <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '10px', color: '#D44F2E', marginTop: '8px' }}>
                    {aiError}
                  </div>
                )}

                {/* AI Preview grid */}
                {aiPreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {aiPreviews.map((url, i) => (
                      <div
                        key={i}
                        onClick={() => applyAIBackground(url, i)}
                        style={{
                          aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                          border: selectedAiPreview === i ? '2px solid #F07B1D' : '1px solid rgba(255,255,255,.08)',
                          background: '#0F1219', position: 'relative',
                          boxShadow: selectedAiPreview === i ? '0 0 12px rgba(240,123,29,.3)' : 'none',
                          transition: 'all .15s',
                        }}
                      >
                        <img
                          src={url}
                          alt={`variation ${i + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => { e.target.style.opacity = '0.3'; }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeLeftTab === 'AR' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '160px', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '28px' }}>✨</span>
                <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', color: '#3A3F4E', letterSpacing: '1px' }}>AR coming soon</span>
              </div>
            )}

            {activeLeftTab === 'TEXT' && (
              <>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
                  Fonts
                </div>
                {FONTS.map(font => (
                  <div
                    key={font.name}
                    onClick={() => applyFont(font)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '9px 10px', borderRadius: '7px', marginBottom: '4px', cursor: 'pointer',
                      background: selectedFont.name === font.name ? 'rgba(240,123,29,0.08)' : '#151A24',
                      border: selectedFont.name === font.name ? '1px solid #F07B1D' : '1px solid rgba(255,255,255,0.055)',
                      transition: 'all .14s',
                    }}
                  >
                    <span style={{ fontFamily: font.family, fontSize: '14px', color: '#EAE0CC', ...font.style }}>{font.name}</span>
                    <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', color: '#72788A' }}>{font.tag}</span>
                  </div>
                ))}
                <button
                  onClick={addText}
                  style={{
                    width: '100%', marginTop: '10px', background: '#1C2230',
                    border: '1px solid rgba(255,255,255,0.055)', borderRadius: '8px',
                    padding: '10px', fontFamily: "'Azeret Mono', monospace", fontSize: '9px',
                    letterSpacing: '1.5px', textTransform: 'uppercase', color: '#72788A', cursor: 'pointer',
                  }}
                >
                  + Add Text
                </button>
              </>
            )}

            {activeLeftTab === 'BG' && (
              <>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
                  Solid
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                  {['#050608','#090B10','#0F1219','#151A24','#1C2230','#3A3F4E','#EAE0CC','#F07B1D'].map(c => (
                    <div
                      key={c}
                      onClick={() => setBackground({ type: 'solid', value: c })}
                      style={{
                        width: '28px', height: '28px', borderRadius: '50%', background: c, cursor: 'pointer',
                        border: background.value === c ? '2px solid #F07B1D' : '2px solid rgba(255,255,255,0.1)',
                      }}
                    />
                  ))}
                </div>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
                  Gradients
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {[
                    { label: 'Midnight', bg: 'linear-gradient(135deg,#0a0a0a,#1a1a2e)' },
                    { label: 'Ember',    bg: 'linear-gradient(135deg,#1a0a00,#4a1a00)' },
                    { label: 'Violet',   bg: 'linear-gradient(135deg,#1a0030,#6a0dad)' },
                    { label: 'Gold',     bg: 'linear-gradient(135deg,#1a1000,#8b6914)' },
                  ].map(g => (
                    <div
                      key={g.label}
                      onClick={() => setBackground({ type: 'gradient', value: g.bg })}
                      style={{
                        height: '40px', borderRadius: '8px', background: g.bg, cursor: 'pointer',
                        border: '1px solid rgba(255,255,255,0.055)', display: 'flex', alignItems: 'flex-end', padding: '4px 6px',
                      }}
                    >
                      <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '7px', color: 'rgba(255,255,255,0.5)' }}>{g.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* CENTER — Canvas area */}
        <div className="lab-canvas-area" style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)`,
          backgroundSize: '24px 24px', backgroundColor: '#0F1219',
          position: 'relative', overflow: 'hidden',
        }}>
          <CanvasWorkspace
            fabricRef={fabricRef}
            canvasWidth={aspectRatio.width}
            canvasHeight={aspectRatio.height}
            background={background}
            onObjectModified={() => saveState()}
            onSelectionChange={setSelectedObject}
            formatLabel={`${activeFormat.label} — ${aspectRatio.width} × ${aspectRatio.height}`}
          />
        </div>

        {/* RIGHT PANEL */}
        <div className="lab-rpanel" style={{
          width: '220px', flexShrink: 0,
          background: '#090B10', borderLeft: '1px solid rgba(255,255,255,0.055)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {/* Section header */}
            <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '3px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '4px' }}>
              Editing
            </div>
            <div style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '13px', color: '#EAE0CC', marginBottom: '14px' }}>
              {selectedObject?.type === 'textbox' ? 'Text Layer' : 'Canvas'}
            </div>

            {/* Font family */}
            <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
              Font
            </div>
            {FONTS.map(font => (
              <div
                key={font.name}
                onClick={() => applyFont(font)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '7px 9px', borderRadius: '6px', marginBottom: '3px', cursor: 'pointer',
                  background: selectedFont.name === font.name ? 'rgba(240,123,29,0.08)' : 'transparent',
                  border: selectedFont.name === font.name ? '1px solid rgba(240,123,29,0.2)' : '1px solid transparent',
                  transition: 'all .12s',
                }}
              >
                <span style={{ fontFamily: font.family, fontSize: '13px', color: '#C8BCA8', ...font.style }}>{font.name}</span>
                <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', color: '#3A3F4E' }}>{font.tag}</span>
              </div>
            ))}

            {/* Alignment */}
            <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: '#3A3F4E', margin: '12px 0 8px' }}>
              Alignment
            </div>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {[{ icon: '≡', align: 'left' }, { icon: '≡', align: 'center' }, { icon: '≡', align: 'right' }].map(({ icon, align }) => (
                <button
                  key={align}
                  onClick={() => {
                    const canvas = fabricRef.current;
                    const active = canvas?.getActiveObject();
                    if (active?.type === 'textbox') { active.set('textAlign', align); canvas.renderAll(); saveState(); }
                  }}
                  style={{
                    flex: 1, padding: '7px', borderRadius: '6px', cursor: 'pointer',
                    background: '#151A24', border: '1px solid rgba(255,255,255,0.055)',
                    color: '#72788A', fontSize: '14px',
                  }}
                >
                  {icon}
                </button>
              ))}
            </div>

            {/* Color swatches */}
            <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>
              Color
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '12px' }}>
              {COLOR_SWATCHES.map(c => (
                <div
                  key={c}
                  onClick={() => applyColor(c)}
                  style={{
                    width: '24px', height: '24px', borderRadius: '50%', background: c, cursor: 'pointer',
                    border: '1.5px solid rgba(255,255,255,0.12)',
                  }}
                />
              ))}
            </div>

            {/* Add text shortcut */}
            <button
              onClick={addText}
              style={{
                width: '100%', padding: '9px', borderRadius: '8px', cursor: 'pointer',
                background: '#151A24', border: '1px solid rgba(255,255,255,0.055)',
                fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '1.5px',
                textTransform: 'uppercase', color: '#72788A', marginBottom: '6px',
              }}
            >
              + Add Text
            </button>
          </div>

          {/* BONITA AI bar */}
          <div style={{
            flexShrink: 0, padding: '10px 14px',
            borderTop: '1px solid rgba(255,255,255,0.055)',
            background: 'linear-gradient(135deg,rgba(124,79,212,.06),rgba(212,79,46,.04))',
          }}>
            <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', textTransform: 'uppercase', color: '#7C4FD4', marginBottom: '6px' }}>
              ✧ BONITA AI
            </div>
            <div style={{ position: 'relative' }}>
              <input
                value={bonitaPrompt}
                onChange={e => setBonitaPrompt(e.target.value)}
                placeholder="'make this gold luxury...'"
                style={{
                  width: '100%', background: '#151A24',
                  border: '1px solid rgba(124,79,212,.2)', borderRadius: '7px',
                  padding: '8px 36px 8px 10px',
                  fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px',
                  color: '#EAE0CC', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button style={{
                position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                width: '24px', height: '24px', background: '#7C4FD4', border: 'none',
                borderRadius: '4px', cursor: 'pointer', color: '#fff', fontSize: '11px',
              }}>→</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── THUMB BAR ── */}
      <div className="lab-thumb-bar" style={{
        height: '72px', background: '#090B10',
        borderTop: '1px solid rgba(255,255,255,0.055)',
        display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px',
        flexShrink: 0, overflowX: 'auto',
      }}>
        <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#3A3F4E', flexShrink: 0 }}>
          Pages
        </span>
        {/* Current page thumbnail */}
        <div style={{
          width: '48px', height: '48px', borderRadius: '7px',
          border: '2px solid #F07B1D', boxShadow: '0 0 10px rgba(240,123,29,.3)',
          cursor: 'pointer', flexShrink: 0, overflow: 'hidden', background: '#151A24',
        }} />
        {/* Add page */}
        <div style={{
          width: '48px', height: '48px', borderRadius: '7px',
          border: '1px dashed #3A3F4E', cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '20px', color: '#3A3F4E', transition: 'all .14s',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#72788A'; e.currentTarget.style.color = '#72788A'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#3A3F4E'; e.currentTarget.style.color = '#3A3F4E'; }}
        >
          +
        </div>
      </div>

      {/* ── MOBILE BOTTOM SHEET ── (hidden on desktop, shown on mobile via CSS) */}
      <div className="mobile-lab-sheet">
        {/* TAB BAR — always visible */}
        <div style={{ display: 'flex', alignItems: 'center', height: '56px', borderTop: '1px solid rgba(255,255,255,0.07)', background: '#090B10', flexShrink: 0, padding: '0 4px' }}>
          {[
            { id: 'photos', icon: '📷', label: 'Photos' },
            { id: 'ai',     icon: '✨', label: 'AI' },
            { id: 'ar',     icon: '🎭', label: 'AR' },
            { id: 'text',   icon: 'T',  label: 'Text' },
            { id: 'bg',     icon: '🎨', label: 'BG' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (mobileActiveTab === tab.id && mobileSheetOpen) {
                  setMobileSheetOpen(false);
                } else {
                  setMobileActiveTab(tab.id);
                  setMobileSheetOpen(true);
                }
              }}
              style={{
                flex: 1, height: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: '3px',
                border: 'none', background: 'transparent', cursor: 'pointer',
                borderTop: mobileActiveTab === tab.id && mobileSheetOpen ? '2px solid #F07B1D' : '2px solid transparent',
                transition: 'all .14s',
              }}
            >
              <span style={{
                fontSize: tab.id === 'text' ? '16px' : '18px',
                fontFamily: tab.id === 'text' ? "'Bebas Neue', sans-serif" : 'inherit',
                fontWeight: tab.id === 'text' ? 700 : 'normal',
                color: mobileActiveTab === tab.id && mobileSheetOpen ? '#F07B1D' : '#72788A',
              }}>{tab.icon}</span>
              <span style={{
                fontFamily: "'Azeret Mono', monospace", fontSize: '8px',
                letterSpacing: '0.5px', textTransform: 'uppercase',
                color: mobileActiveTab === tab.id && mobileSheetOpen ? '#F07B1D' : '#72788A',
              }}>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* SHEET CONTENT — slides up */}
        <div style={{
          background: '#090B10', borderTop: '1px solid rgba(255,255,255,0.07)',
          overflow: 'hidden', transition: 'max-height 0.32s cubic-bezier(0.4,0,0.2,1)',
          maxHeight: mobileSheetOpen ? '50vh' : '0px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
            <div style={{ width: '32px', height: '3px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px' }} />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 'calc(50vh - 24px)', padding: '8px 12px 20px', WebkitOverflowScrolling: 'touch' }}>

            {/* PHOTOS TAB */}
            {mobileActiveTab === 'photos' && (
              <div>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>Templates</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px', marginBottom: '14px' }}>
                  {PHOTO_TEMPLATES.map(t => (
                    <div
                      key={t.label}
                      onClick={() => { applyDesignToCanvas(t.preset); setMobileSheetOpen(false); }}
                      style={{ aspectRatio: '1', borderRadius: '8px', background: t.bg, border: '1px solid rgba(255,255,255,.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}
                    >
                      <span style={{ fontSize: '20px' }}>{t.thumbText}</span>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '3px 4px', background: 'rgba(0,0,0,.65)', fontFamily: "'Azeret Mono', monospace", fontSize: '7px', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,.7)', textAlign: 'center' }}>{t.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>Add Photo</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                  {[
                    { icon: '📷', label: 'Camera',  onClick: () => { const i = document.createElement('input'); i.type='file'; i.accept='image/*'; i.capture='environment'; i.onchange=(e)=>handleMediaFile(e.target.files[0]); i.click(); } },
                    { icon: '🎥', label: 'Video',   onClick: () => { const i = document.createElement('input'); i.type='file'; i.accept='video/*'; i.capture='environment'; i.onchange=(e)=>handleVideoFile(e.target.files[0]); i.click(); } },
                    { icon: '🗂', label: 'Gallery', onClick: () => { const i = document.createElement('input'); i.type='file'; i.accept='image/*,video/*'; i.onchange=(e)=>{ const f=e.target.files[0]; f?.type?.startsWith('video/') ? handleVideoFile(f) : handleMediaFile(f); }; i.click(); } },
                  ].map(btn => (
                    <button key={btn.label} onClick={btn.onClick} style={{ padding: '10px 6px', background: '#151A24', border: '1px solid rgba(255,255,255,.07)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                      <span style={{ fontSize: '20px' }}>{btn.icon}</span>
                      <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1px', textTransform: 'uppercase', color: '#72788A' }}>{btn.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* AI TAB */}
            {mobileActiveTab === 'ai' && (
              <div>
                <textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder={"'dramatic HBCU campus golden hour'"}
                  style={{ width: '100%', background: '#151A24', border: '1px solid rgba(124,79,212,.25)', borderRadius: '8px', padding: '10px', fontFamily: "'Instrument Sans', sans-serif", fontSize: '14px', color: '#EAE0CC', outline: 'none', resize: 'none', minHeight: '70px', marginBottom: '10px' }}
                />
                <button
                  onClick={handleAIGenerate}
                  disabled={aiLoading || !aiPrompt.trim()}
                  style={{ width: '100%', background: '#7C4FD4', color: '#fff', fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', letterSpacing: '2px', border: 'none', borderRadius: '10px', padding: '14px', cursor: 'pointer', marginBottom: '10px', opacity: !aiPrompt.trim() ? 0.5 : 1 }}
                >
                  {aiLoading ? '◌ Generating...' : '✧ Generate'}
                </button>
                {aiPreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {aiPreviews.map((url, i) => (
                      <div key={i} onClick={() => { applyAIBackground(url, i); setMobileSheetOpen(false); }}
                        style={{ aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', border: selectedAiPreview === i ? '2px solid #F07B1D' : '1px solid rgba(255,255,255,.08)', cursor: 'pointer', background: '#151A24' }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TEXT TAB */}
            {mobileActiveTab === 'text' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {FONTS.map(font => (
                  <button key={font.name}
                    onClick={() => { applyFont(font); addText(); setMobileSheetOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#151A24', border: '1px solid rgba(255,255,255,.07)', borderRadius: '8px', cursor: 'pointer' }}>
                    <span style={{ fontFamily: font.family, fontSize: '16px', color: '#EAE0CC', ...font.style }}>{font.name}</span>
                    <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', color: '#72788A' }}>+ Add</span>
                  </button>
                ))}
              </div>
            )}

            {/* BG TAB */}
            {mobileActiveTab === 'bg' && (
              <div>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>Solid Colors</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                  {['#050608','#0F1219','#1C2230','#1A1100','#14001A','#0A1020','#FFFFFF','#EAE0CC','#F07B1D','#C9950C','#18B574','#7C4FD4'].map(c => (
                    <div key={c} onClick={() => setBackground({ type: 'solid', value: c })}
                      style={{ width: '36px', height: '36px', background: c, borderRadius: '6px', border: '1px solid rgba(255,255,255,.1)', cursor: 'pointer' }} />
                  ))}
                </div>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>Gradients</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '6px' }}>
                  {['linear-gradient(135deg,#1A1100,#2C1E00)','linear-gradient(135deg,#0A0014,#1A0030)','linear-gradient(135deg,#0D1A12,#0A2016)','linear-gradient(135deg,#1A0800,#301400)','linear-gradient(160deg,#FF6B35,#F7931E,#FFD700)','linear-gradient(135deg,#0A1020,#182840)','linear-gradient(135deg,#14000A,#280014)','radial-gradient(circle at 30% 30%,#2A1A00,#050608)'].map((g, i) => (
                    <div key={i} onClick={() => setBackground({ type: 'gradient', value: g })}
                      style={{ height: '36px', background: g, borderRadius: '6px', border: '1px solid rgba(255,255,255,.08)', cursor: 'pointer' }} />
                  ))}
                </div>
              </div>
            )}

            {/* AR TAB */}
            {mobileActiveTab === 'ar' && (
              <div>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#3A3F4E', marginBottom: '8px' }}>Stickers</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '6px' }}>
                  {['🔥','👑','💜','🎙','📰','🌍','⚡','🤝','🎓','🍽','✊🏾','💫'].map(emoji => (
                    <button key={emoji} onClick={() => { addArSticker(emoji); setMobileSheetOpen(false); }}
                      style={{ aspectRatio: '1', background: '#151A24', border: '1px solid rgba(255,255,255,.07)', borderRadius: '8px', fontSize: '24px', cursor: 'pointer' }}>
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Composer Modal */}
      <ComposerModal
        open={composerOpen}
        onOpenChange={(val) => {
          setComposerOpen(val);
          if (!val) setComposerMedia(null);
        }}
        initialMedia={composerMedia}
      />
    </div>
  );
};

export default LabPage;
